import { fetchEventSource } from '@microsoft/fetch-event-source';
import { RefreshDataEventType } from 'PublicEntityTypes';
import { createContext, ReactNode, useEffect, useRef, useState } from 'react';
import type { AuditLogView } from '../../types/entity-types';
import { useUserInfo } from '../user-context/use-user-info';
import { handleAuditLogEvent } from './handle-audit-log-event';
import { isAuditLogEvent } from './is-audit-log-event';

interface SSEContextType {
  lastRefreshEvent: RefreshDataEventType | null;
}

const SSEContext = createContext<SSEContextType | null>(null);

type SSEProviderProps = {
  children: ReactNode;
};

export const SSEProvider = ({ children }: SSEProviderProps) => {
  const [lastAuditLogEvent, setLastAuditLogEventEvent] = useState<AuditLogView | null>(null);
  const [lastRefreshEvent, setLastRefreshEvent] = useState<RefreshDataEventType | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const initialConnectionMadeRef = useRef(false);
  const { isAuthenticated, authenticatedInfo } = useUserInfo();

  useEffect(() => {
    if (!abortControllerRef.current) {
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      return;
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current?.abort();
        abortControllerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const connectSSE = async () => {
      if (!abortControllerRef.current) {
        return;
      }

      if (!isAuthenticated || !authenticatedInfo?.emailVerified || !authenticatedInfo?.isOrgReady) {
        return;
      }

      let url = '/api/sse/stream';
      if (lastAuditLogEvent && isAuditLogEvent(lastAuditLogEvent)) {
        url += `?since=${lastAuditLogEvent.timestamp}`;
      }
      await fetchEventSource(url, {
        signal: abortControllerRef.current.signal,
        headers: {
          'Content-Type': 'application/json',
        },
        // openWhenHidden true to avoid SSE connection closing when the browser tab is not visible
        // https://github.com/Azure/fetch-event-source/issues/75
        openWhenHidden: true,
        onmessage(event) {
          // Prevents warning console log about event handler taking too long
          // https://developer.mozilla.org/en-US/docs/Web/API/Window/queueMicrotask
          queueMicrotask(() => {
            const data = JSON.parse(event.data);
            // Just in case we get duplicate events, which can happen on the controller or if there is a page refresh etc.
            if (![lastAuditLogEvent?.type, lastRefreshEvent?.type].includes(data.type)) {
              if (isAuditLogEvent(data)) {
                setLastAuditLogEventEvent(data);

                return;
              }

              setLastRefreshEvent(data);
            }
          });
        },

        onerror(err) {
          // If the connection is aborted, we don't want to retry
          if (abortControllerRef.current?.signal.aborted) {
            throw err;
          }

          /**
           * Normally SSE API will return text/event-stream, however in unauthorized cases, it returns JSON
           * This is a guardrail to prevent the retry logic from happening, the unauthorized cases can happen if the cookie is expired
           * and since "openWhenHidden" is true, the connection will try to reconnect even if the tab is not visible
           */
          if (
            err?.message === 'Expected content-type to be text/event-stream, Actual: application/json; charset=utf-8'
          ) {
            throw err;
          }

          setTimeout(connectSSE, 5000);

          // @see https://github.com/Azure/fetch-event-source/issues/46#issuecomment-1606721854
          throw err;
        },
      });
    };

    // Prevents new connections after the component is mounted
    if (!initialConnectionMadeRef.current) {
      initialConnectionMadeRef.current = true;
      connectSSE();
    }
    // We disable the exhaustive-deps rule because we only want to establish a single connection
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (lastAuditLogEvent && lastAuditLogEvent.isUserFacing) {
      handleAuditLogEvent({
        type: lastAuditLogEvent.type,
        message: lastAuditLogEvent.message,
        isError: lastAuditLogEvent.isError,
      });
    }
  }, [lastAuditLogEvent]);

  return <SSEContext.Provider value={{ lastRefreshEvent }}>{children}</SSEContext.Provider>;
};

SSEProvider.SSEContext = SSEContext;
